import {
  ILanguages,
  IListingPhotos,
  IObjectsHasTags,
  ISuiteTypesFloorplans,
} from '@llws/lift-entity-interfaces';
import { IApiCommandResponse } from '@llws/api-common';
import { IQueryCommandOptions } from '@llws/dynamic-router-utils';
import {
  IApiResponse, IEditImage, IModifyPayload,
} from '@/shared';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import uploads from '@/shared/services/uploads';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { getByModelAndObjectId } from '@/shared/services/tags';
import { INewTranslationSettingsStoreConfig } from '@/shared/interfaces/new-translation-settings';
import { getNewUnitFloorplan } from '@/shared/factory/new-unit-floorplan';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IFilterPayloadV2 } from '@/shared/interfaces/filter-payload-v2';

export interface IListingFloorplanWithExtraData extends ISuiteTypesFloorplans {
  languages: ILanguages[];
  objectsHasTags: IObjectsHasTags[];
}

export interface ICustomListingFloorplanState extends IStateStrict<ISuiteTypesFloorplans> {
  singleWithExtraData: IListingFloorplanWithExtraData | null;
}

// Base store configuration
const adminGatewayResourcePath: string = '/property-api/units/:param/floorplans';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  limit: 999,
  orderBy: [{ field: 'orderBy', direction: 'asc' }],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};

const translationSettings: INewTranslationSettingsStoreConfig = {
  model: 'suite_type_floorplans',
  keys: ['name'],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ISuiteTypesFloorplans>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
  translationSettings,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'unit-media-floorplans/'

  state: {
    ...base.state,
    singleWithExtraData: null,
  },

  getters: {
    ...base.getters,
    singleWithExtraData: (state: ICustomListingFloorplanState) => state.singleWithExtraData,
  },

  mutations: {
    ...base.mutations,
    setSingleWithExtraData: (state: ICustomListingFloorplanState, payload: IListingFloorplanWithExtraData) => {
      state.singleWithExtraData = payload;
    },
  },

  actions: {
    ...base.actions,
    uploadAndSave: async (
      ctx: IAction<IStateStrict<IListingPhotos>>,
      payload: IModifyPayload<number, { pdf?: File, image?: File, name: string, isEnabled: boolean }, { clientKey: string, clientId: number, orderBy: number }>,
    ) => {
      const { pdf, name, image, isEnabled } = payload.data;
      if ((!pdf && !image) || !payload.parentId) return null;

      let pdfFileName = '';
      let imageFileName = '';
      let height: number = 0;
      let width: number = 0;

      if (pdf) {
        const uploadResult = await uploads.uploadSingleFile(
          payload.additional!.clientKey,
          'floorplans',
          pdf,
        );
        pdfFileName = uploadResult.filename.split('/').pop()!;
      }
      if (image) {
        const uploadResult = await uploads.uploadSingleFile(
          payload.additional!.clientKey,
          'floorplans',
          image,
        );
        imageFileName = uploadResult.filename.split('/').pop()!;
        height = parseInt(uploadResult.height || '0', 10);
        width = parseInt(uploadResult.width || '0', 10);
      }

      const floorplansResponse: IApiResponse<ISuiteTypesFloorplans[], null> = await adminRequest.post(
        `/property-api/units/${payload.parentId}/floorplans`,
        {
          ...getNewUnitFloorplan(
            payload.additional!.clientId,
            payload.parentId!,
            imageFileName,
            pdfFileName,
            height,
            width,
            name,
            isEnabled ? 1 : 0,
            payload.parentId,
          ),
          orderBy: payload.additional?.orderBy,
        },
      );

      ctx.commit('set', [ ...ctx.getters.list, floorplansResponse.data![0]! ]);
      return floorplansResponse.data![0]!;
    },

    getWithTagsAndTranslations: async (
      ctx: IAction<IStateStrict<ISuiteTypesFloorplans>>,
      payload: IFilterPayloadV2<number>,
    ) => {
      await ctx.dispatch('get', {
        ...payload,
        ...{ additional: { includeTranslations: true } },
      } as IFilterPayloadV2<number>);

      const tagsResponse = await getByModelAndObjectId('suite_types_floorplans', ctx.getters.single.id);

      ctx.commit('setSingleWithExtraData', { ...ctx.getters.single, ...{ objectsHasTags: tagsResponse.data || [] } });
    },

    updateMany: async (
      ctx: IAction<IStateStrict<IListingPhotos>>,
      payload: IModifyPayload<number, ISuiteTypesFloorplans[]>,
    ): Promise<ISuiteTypesFloorplans[] | undefined | null> => {
      const floorplansResponse: IApiResponse<ISuiteTypesFloorplans[], null> = await adminRequest.put(
        `/property-api/units/${payload.parentId}/floorplans`,
        payload.data,
      );

      return floorplansResponse.data;
    },

    saveUploadedImageEdits: async (
      ctx: IAction<IStateStrict<ISuiteTypesFloorplans>>,
      payload: IModifyPayload<number, ISuiteTypesFloorplans, { clientKey: string, filename: string, imageEdits: IEditImage[] }>,
    ): Promise<IListingFloorplanWithExtraData> => {
      const uploadEditResult = await uploads.updateImageAsNew(
        payload.additional!.clientKey,
        payload.additional!.filename,
        'floorplans',
        payload.additional!.imageEdits,
      );

      // Save it to the database
      const updatedPhoto: IApiResponse<ISuiteTypesFloorplans, null> = await adminRequest.put(
        `/property-api/units/${payload.parentId}/floorplans/${payload.id}`,
        {
          image: uploadEditResult.filename.split('/').pop(),
        },
      );

      const newPhoto = { ...ctx.getters.single, ...updatedPhoto.data! };
      ctx.commit('set', newPhoto);
      return newPhoto;
    },

    deleteFloorplan: async (
      ctx: IAction<IStateStrict<ISuiteTypesFloorplans>>,
      payload: IModifyPayload<number, number>,
    ): Promise<boolean> => {
      // Save it to the database
      const deleteFloorplan: IApiCommandResponse<ISuiteTypesFloorplans| undefined> = await adminRequest.delete(
        `/property-api/units/${payload.parentId}/floorplans`,
        [payload.data],
      );
      return !!deleteFloorplan;
    },

  },
};
